/**
 * PhysicalVerifyStep — Step 3 of the physical checkout variant.
 *
 * Renders:
 *   - Total-to-debit card with the 72h-seller-timeout explanation.
 *   - Inline error alert + footer (Atrás → Step 2 / Confirmar y crear
 *     orden → calls `callCreate`, anti-P-043 disables button while
 *     submitting).
 *
 * Founder directive (2026-11): the 2FA-verified banner was removed —
 * the verify step is now a plain confirmation step (no 2FA prerequisite).
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * Preserved VERBATIM — the original Step 3 JSX block (lines 1630-1708 of
 * the monolith).
 */

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Loader2,
  ArrowLeft,
  ShoppingBag,
} from 'lucide-react';
import { formatCredits } from '../helpers';
import { SubmitErrorAlert } from './submit-error-alert';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Estimate {
  unitPrice: number;
  totalPrice: number;
  shippingCost: number;
  grandTotal: number;
  freeShippingApplied: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface PhysicalVerifyStepProps {
  estimate: Estimate;
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  onCreate: () => void;
}

export function PhysicalVerifyStep({
  estimate,
  submitting,
  submitError,
  onBack,
  onCreate,
}: PhysicalVerifyStepProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
        <div className="flex justify-between mb-2">
          <span className="text-muted-foreground">Total a debitar</span>
          <span className="text-lg font-bold tabular-nums">
            {formatCredits(estimate.grandTotal)} créditos
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Los créditos se transferirán al escrow de la orden. El
          vendedor tiene 72h para confirmar; si no lo hace, se te
          reembolsan automáticamente.
        </p>
      </div>

      {submitError && <SubmitErrorAlert submitError={submitError} />}

      <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex-row gap-2 border-t border-border bg-background px-6 py-3 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex-1"
          disabled={submitting}
        >
          <ArrowLeft className="size-4 mr-1" aria-hidden="true" />
          Atrás
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={onCreate}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 mr-1 animate-spin" aria-hidden="true" />
              Creando…
            </>
          ) : (
            <>
              <ShoppingBag className="size-4 mr-1" aria-hidden="true" />
              Confirmar y crear orden
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}
